import { Box, Button, Center, Flex, Group, Loader, NumberInput, Paper, SimpleGrid, Text, Title } from "@mantine/core";
import { IconPencil } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { successNotification } from "@/shared/utils/successNotification";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";

// ─── Config ───────────────────────────────────────────────────────────────────

type InflationField =
  | "retirementInflationRate"
  | "IndianEducationInflationRate"
  | "ForeignEducationInflationRate"
  | "weddingInflationRate"
  | "SwpRelevtiveInflationRate"
  | "FireInflationRate";

type CardConfig = {
  field: InflationField;
  title: string;
  description: string;
  fieldLabel: string;
};

const INFLATION_CARDS: CardConfig[] = [
  {
    field: "retirementInflationRate",
    title: "Post-retirement inflation rate",
    description: "This rate is used to calculate expenses and projections after retirement.",
    fieldLabel: "Post-retirement inflation (%)",
  },
  {
    field: "IndianEducationInflationRate",
    title: "Annual Education Inflation Rate (India)",
    description:
      "This rate is used to estimate the future cost of education in India based on expected annual inflation.",
    fieldLabel: "Annual Education Inflation (%) \u2013 India",
  },
  {
    field: "ForeignEducationInflationRate",
    title: "Annual Education Inflation Rate (Abroad)",
    description:
      "This rate is used to estimate the future cost of education abroad based on expected annual inflation.",
    fieldLabel: "Annual Education Inflation (%) \u2013 Abroad",
  },
  {
    field: "weddingInflationRate",
    title: "Wedding cost inflation rate",
    description: "This rate helps calculate the projected wedding cost considering annual inflation.",
    fieldLabel: "Wedding Cost Inflation (%)",
  },
  {
    field: "SwpRelevtiveInflationRate",
    title: "SWP Relative Inflation Rate",
    description:
      "This rate helps calculate the expected value of your withdrawals, considering relative inflation over time.",
    fieldLabel: "SWP Relative Inflation (%)",
  },
  {
    field: "FireInflationRate",
    title: "Fire Inflation Rate",
    description:
      "This rate is used to calculate the expected value of your expenses at the time of financial independence, considering inflation over time.",
    fieldLabel: "Fire Inflation (%)",
  },
];

// ─── InflationCard ────────────────────────────────────────────────────────────

type CardState = {
  value: number;
  editValue: number;
  editing: boolean;
  saving: boolean;
};

function InflationCard({
  config,
  state,
  disabled,
  onChange,
  onEdit,
  onSave,
}: {
  config: CardConfig;
  state: CardState;
  disabled: boolean;
  onChange: (value: number) => void;
  onEdit: () => void;
  onSave: () => void;
}) {
  return (
    <Paper radius="sm" bg="#F9F8F8" style={{ display: "flex", flexDirection: "column", opacity: disabled ? 0.7 : 1 }}>
      {/* Title + description — grows to fill available space */}
      <Box px="sm" pt="md" style={{ flex: 1 }}>
        <Text fw={600} fz="sm" mb={4}>
          {config.title}
        </Text>
        <Text fz="xs" c="slate.5" style={{ lineHeight: 1.5 }}>
          {config.description}
        </Text>
      </Box>

      {/* Input — always at the same vertical position */}
      <Box px="sm" pt="md">
        <NumberInput
          label={config.fieldLabel}
          value={state.editing ? state.editValue : state.value}
          onChange={(v) => onChange(typeof v === "number" ? v : Number(v))}
          readOnly={!state.editing}
          disabled={disabled}
          min={0}
          max={100}
          decimalScale={2}
          radius={10}
          styles={{
            input: {
              height: 42,
              paddingLeft: 16,
              paddingRight: 16,
              backgroundColor: "#FFFFFF",
              borderColor: "#BFBFBF",
              cursor: state.editing ? "text" : "default",
            },
          }}
        />
      </Box>

      {/* Button — always at the bottom */}
      <Box px="sm" py="md">
        <Group justify="flex-end">
          {state.editing ? (
            <Button color="primary" onClick={onSave} loading={state.saving}>
              Save
            </Button>
          ) : (
            <Button
              variant="outline"
              color="primary"
              leftSection={<IconPencil size={13} />}
              onClick={onEdit}
              disabled={disabled}
            >
              Edit
            </Button>
          )}
        </Group>
      </Box>
    </Paper>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function InflationConfigPage() {
  const [loading, setLoading] = useState(true);
  const [cardStates, setCardStates] = useState<Record<InflationField, CardState>>(
    () =>
      Object.fromEntries(
        INFLATION_CARDS.map((c) => [c.field, { value: 0, editValue: 0, editing: false, saving: false }])
      ) as Record<InflationField, CardState>
  );

  useEffect(() => {
    const fetchRates = async () => {
      setLoading(true);
      try {
        const res = await trpc.maintenance.getInflationRates.query();
        setCardStates((prev) => {
          const updated = { ...prev };
          for (const field of Object.keys(res.data) as InflationField[]) {
            const v = (res.data as Record<string, number>)[field] ?? 0;
            updated[field] = { ...updated[field], value: v, editValue: v };
          }
          return updated;
        });
      } catch (err) {
        handleTrpcError(err);
      } finally {
        setLoading(false);
      }
    };
    fetchRates();
  }, []);

  const handleEdit = (field: InflationField) => {
    setCardStates((prev) => ({
      ...prev,
      [field]: { ...prev[field], editing: true, editValue: prev[field].value },
    }));
  };

  const handleChange = (field: InflationField, value: number) => {
    setCardStates((prev) => ({ ...prev, [field]: { ...prev[field], editValue: value } }));
  };

  const handleSave = async (field: InflationField) => {
    const { editValue } = cardStates[field];
    setCardStates((prev) => ({ ...prev, [field]: { ...prev[field], saving: true } }));
    try {
      await trpc.maintenance.updateInflationRate.mutate({ field, value: editValue });
      setCardStates((prev) => ({
        ...prev,
        [field]: { ...prev[field], value: editValue, editing: false, saving: false },
      }));
      successNotification({ title: "Inflation Updated", message: "Inflation rate updated successfully." });
    } catch (err) {
      handleTrpcError(err);
      setCardStates((prev) => ({ ...prev, [field]: { ...prev[field], saving: false } }));
    }
  };

  if (loading) {
    return (
      <Center h="100vh">
        <Loader size="lg" type="dots" />
      </Center>
    );
  }

  return (
    <Flex direction="column" w="100%" p="xl">
      <Title order={2} fw={700} mb="xl">
        Inflation configuration
      </Title>

      <SimpleGrid cols={3} spacing="lg">
        {INFLATION_CARDS.map((config) => {
          const isAnyEditing = Object.values(cardStates).some((card) => card.editing);
          const disabled = isAnyEditing && !cardStates[config.field].editing;

          return (
            <InflationCard
              key={config.field}
              config={config}
              state={cardStates[config.field]}
              disabled={disabled}
              onChange={(v) => handleChange(config.field, v)}
              onEdit={() => handleEdit(config.field)}
              onSave={() => handleSave(config.field)}
            />
          );
        })}
      </SimpleGrid>
    </Flex>
  );
}
